import { useAppDispatch, useAppSelector } from "@/lib/store";
import { useEffect, useCallback, useRef } from "react";
import {
  fetchVendorAppointments,
  fetchVendorAppointmentFilterOptions,
  setAssignee,
  setPage,
  setPageSize,
  setSearch,
  setServiceParent,
  setSortKey,
  setStatus,
  updateAppointmentInList,
} from "../../appointments/appointments.slice";

export const useVendorAppointments = (enabled: boolean = true) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((s) => s.vendorAppointments);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    const fetchData = async () => {
      try {
        await dispatch(fetchVendorAppointments(undefined)).unwrap();
      } catch (error: any) {
        // Ignore cancelled requests
        if (error.message !== "Request cancelled") {
          console.error("Failed to fetch appointments:", error);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    enabled,
    dispatch,
    state.filters.status,
    state.filters.serviceParent,
    state.filters.assignee,
    state.filters.search,
    state.filters.sortKey,
    state.filters.sortOrder,
    state.pagination.page,
    state.pagination.pageSize,
  ]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    dispatch(fetchVendorAppointmentFilterOptions());
  }, [enabled, dispatch]);

  // Memoize filter setters to prevent unnecessary re-renders
  const setStatusCallback = useCallback((v: string) => dispatch(setStatus(v)), [dispatch]);
  const setServiceParentCallback = useCallback((v: string) => dispatch(setServiceParent(v)), [dispatch]);
  const setAssigneeCallback = useCallback((v: string) => dispatch(setAssignee(v)), [dispatch]);
  const setSearchCallback = useCallback((v: string) => dispatch(setSearch(v)), [dispatch]);
  const setSortKeyCallback = useCallback((k: string) => dispatch(setSortKey(k)), [dispatch]);
  const setPageCallback = useCallback((p: number) => dispatch(setPage(p)), [dispatch]);
  const setPageSizeCallback = useCallback((ps: number) => dispatch(setPageSize(ps)), [dispatch]);
  const refreshAppointments = useCallback(() => {
    dispatch(fetchVendorAppointments(undefined));
  }, [dispatch]);

  return {
    list: state.list,
    total: state.total,
    loading: state.loading,
    options: state.options,

    filters: {
      ...state.filters,
      setStatus: setStatusCallback,
      setServiceParent: setServiceParentCallback,
      setAssignee: setAssigneeCallback,
      setSearch: setSearchCallback,
      setSortKey: setSortKeyCallback,
    },

    pagination: {
      ...state.pagination,
      setPage: setPageCallback,
      setPageSize: setPageSizeCallback,
    },

    // actions
    updateAppointmentInList: (appointment: any) => dispatch(updateAppointmentInList(appointment)),
    refreshAppointments,

    dispatch,
  };
};
